/* eslint-disable jsx-a11y/media-has-caption */
import React, { useRef, useState, useEffect, ReactNode } from "react";

import { useReactMediaRecorder } from "react-media-recorder";

import { Button, ModalDialog, SelectField, Form, Loader } from "@culturehq/components";
import { SelectValueWithCategory } from "@culturehq/components/dist/components/form/typings";

import { useDoor } from "app/hooks";
import { t, Trans } from "app/utils";

import { HandleMediaChange } from "components/stories/post-stories-v2/types";

import Timer from "./Timer";
import ChqModalDialog from "./ChqModalDialog";
import Prompter from "./Prompter/Prompter";
import GetVideoThumbnail from "./GetVideoThumbnail";

const INITIAL_COUNTDOWN_VALUE = 5;

interface RegularCameraRecorderProps {
  className?: string;
  initialOpen?: boolean;
  onVideoRecorded: (file: HandleMediaChange) => Promise<void>;
  script?: string;
  children?: ReactNode;
  onOpen?: () => void;
}

const RegularCameraRecorder: React.FC<RegularCameraRecorderProps> = ({
  children, className, initialOpen = false, onVideoRecorded, script = undefined,
  onOpen: onParentOpen
}) => {
  const { open, onOpen, onClose } = useDoor(initialOpen);
  const [audioDevices, setAudioDevices] = useState<{ label: any; value: any }[]>([]);
  const [videoDevices, setVideoDevices] = useState<{ label: any; value: any }[]>([]);
  const [askingPermission, setAskingPermission] = useState(false);
  const [videoConstraints, setVideoConstraints] = useState<any>(false);
  const [audioConstraints, setAudioConstraints] = useState<any>(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [videoEditorOpen, setVideoEditorOpen] = useState(false);
  const [countdown, setCountdown] = useState<number | undefined>();
  const [screenRecord, setScreenRecord] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [persmissionRequested, setPermissionsRequested] = useState(false);
  const videoRef = useRef<any>(null);
  const [selectedAudioDeviceState, setSelectedAudioDeviceState] = useState<any>("");
  const [selectedVideoDeviceState, setSelectedVideoDeviceState] = useState<any>("");
  const [showScript, setShowScript] = useState(!!script);
  const [previewScript, setPreviewScript] = useState(false);
  const [editableScript, setEditableScript] = useState(script || "");
  const {
    status,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    mediaBlobUrl,
    previewStream,
    clearBlobUrl
  } = useReactMediaRecorder({
    screen: screenRecord,
    video: videoConstraints,
    audio: audioConstraints
  });

  useEffect(() => {
    if (!open || !onParentOpen) return;

    onParentOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    let isMounted = true;
    const getDevicesByKind = (devices: any[], kind: string) => devices
      .filter(device => device.kind === kind)
      .map(({ label, deviceId }) => ({ label, value: deviceId }));

    const getDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      if (!open) return;
      setAudioDevices(getDevicesByKind(devices, "audioinput"));
      setVideoDevices(getDevicesByKind(devices, "videoinput"));
    };

    if (!askingPermission && status === "acquiring_media" && !persmissionRequested) {
      setAskingPermission(true);
    }

    if (askingPermission && status === "recording" && !persmissionRequested) {
      setPermissionsRequested(true);
      getDevices();
    }

    return () => {
      isMounted = false;
    };
  }, [askingPermission, open, persmissionRequested, status]);

  useEffect(() => {
    const onEndedEventListener = () => {
      stopRecording();
      setVideoEditorOpen(true);
    };
    const isVideoTrackRecording = screenRecord && status === "recording";
    const videoTrack = isVideoTrackRecording && previewStream?.getVideoTracks()?.[0];
    if (videoTrack) videoTrack.addEventListener("ended", onEndedEventListener);
    return () => {
      if (videoTrack) videoTrack.removeEventListener("ended", onEndedEventListener);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenRecord, status]);

  useEffect(() => {
    const isCountdownChecked = (countdown || 0) > 0 && (countdown || 0) <= INITIAL_COUNTDOWN_VALUE;
    const timeoutId = isCountdownChecked
      ? setTimeout(() => setCountdown((countdown || 0) - 1), 1000) : null;
    if (countdown === 0) {
      startRecording();
      setCountdown(undefined);
      setShowCountdown(false);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  useEffect(() => {
    if (open) {
      stopRecording();
      clearBlobUrl();
      if (!screenRecord) {
        startRecording();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenRecord, open]);

  useEffect(() => {
    if (videoRef.current && previewStream && !showCountdown) {
      videoRef.current.srcObject = previewStream;
    }
  }, [previewStream, showCountdown]);

  useEffect(() => {
    if (open && videoConstraints.deviceId && audioConstraints.deviceId) {
      startRecording();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoConstraints.deviceId, audioConstraints.deviceId]);

  useEffect(() => {
    let isMounted = true;
    const getDevicesByKind = (devices: any[], kind: string) => devices
      .filter(device => device.kind === kind)
      .map(({ label, deviceId }) => ({ label, value: deviceId }));
    const getDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      if (!isMounted) return;
      setAudioDevices(getDevicesByKind(devices, "audioinput"));
      setVideoDevices(getDevicesByKind(devices, "videoinput"));
    };
    getDevices();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setVideoConstraints(
      videoDevices.length > 0 && {
        deviceId: videoDevices[0].value,
        width: { max: 1920, min: 1080 },
        height: { max: 1080, min: 720 }
      }
    );
  }, [videoDevices]);

  useEffect(() => {
    setAudioConstraints(
      audioDevices.length > 0 && { deviceId: audioDevices[0].value }
    );
  }, [audioDevices]);

  const selectRecordMethod = (value: string | SelectValueWithCategory | null) => {
    setVideoConstraints({ width: window.screen.width, height: window.screen.height });
    setScreenRecord(value === "screen");
  };

  const selectedAudioDevice = (value: string | SelectValueWithCategory | null) => {
    stopRecording();
    setAudioConstraints({ deviceId: value });
    setSelectedAudioDeviceState(value);
  };

  const selectedVideoDevice = (value: string | SelectValueWithCategory | null) => {
    stopRecording();
    setVideoConstraints({
      deviceId: value,
      width: { max: 1920, min: 1080 },
      height: { max: 1080, min: 720 }
    });
    setSelectedVideoDeviceState(value);
  };

  const audioValueInit = selectedAudioDeviceState || audioDevices[0]?.label;
  const videoValueInit = selectedVideoDeviceState || videoDevices[0]?.label;
  const onCloseModal = () => {
    onClose();
    resetState();
    stopRecording();
  };

  const startRecord = () => {
    if (screenRecord) {
      setShowFullscreen(true);
      startRecording();
    } else if (!showScript) {
      stopRecording();
      clearBlobUrl();
      setShowCountdown(true);
      setShowFullscreen(true);
      setCountdown(INITIAL_COUNTDOWN_VALUE);
    } else {
      setShowFullscreen(true);
      setIsPaused(true);
      setPreviewScript(true);
    }
  };

  const resetState = () => {
    setShowCountdown(false);
    setShowFullscreen(false);
    setCountdown(undefined);
    setIsPaused(false);
    setVideoEditorOpen(false);
    setScreenRecord(false);
    setShowScript(!!script);
    setPreviewScript(false);
  };

  const finishRecord = () => {
    stopRecording();
    setTimeout(() => {
      setVideoEditorOpen(true);
    }, 1000);
  };

  const handleVideoEdited = async (thumb: Blob) => {
    setVideoEditorOpen(false);
    if (!mediaBlobUrl) return;

    const videoBlob = await fetch(mediaBlobUrl).then(file => file.blob());
    const duration: number = await new Promise(resolve => {
      const video = document.createElement("video");

      video.onloadedmetadata = () => {
        if (video.duration === Infinity) {
          video.currentTime = Number.MAX_SAFE_INTEGER;
          video.ontimeupdate = () => {
            video.ontimeupdate = null;
            video.currentTime = 0;
            resolve(video.duration);
            URL.revokeObjectURL(video.src);
          };
        } else {
          resolve(video.duration);
          URL.revokeObjectURL(video.src);
        }
      };

      video.src = URL.createObjectURL(videoBlob);
    });
    onVideoRecorded({ media: videoBlob, thumb, isRecordedVideo: true, duration });
    onCloseModal();
    clearBlobUrl();
  };

  const handlePause = () => {
    if (script) {
      setPreviewScript(false);
    }
    setIsPaused(true);
    pauseRecording();
  };

  const handleResume = () => {
    if (script) {
      setPreviewScript(false);
    }
    setIsPaused(false);
    resumeRecording();
  };

  const handleShowScriptChange = (value: string | SelectValueWithCategory | null) => {
    setShowScript(value === "yes");
  };

  return (
    <>
      {open && (
        <ChqModalDialog entrance="zoomIn" onClose={onCloseModal} className={`camera-recorder-modal ${showFullscreen ? "camera-recorder-modal-full" : ""}`} ariaLabelledBy="camera-recorder-title" ariaDescribedby="camera-recorder-title">
          {!showFullscreen && (
            <ModalDialog.Heading onClose={onCloseModal}>
              <h2 id="camera-recorder-title"><Trans>Record video or screen</Trans></h2>
            </ModalDialog.Heading>
          )}
          {showCountdown && !showScript && (
            <h1 className="Video-Top_Info">
              <Trans>Recording in</Trans>
              <br />
              <span className="Video-Top_Info--Counter">{countdown}</span>
            </h1>
          )}
          <ModalDialog.Body>
            {!showFullscreen && (
              <Form
                initialValues={{
                  recordOption: "camera",
                  audio: audioConstraints.deviceId || (audioDevices.length > 0 ? audioDevices[0].value : ""),
                  video: videoConstraints.deviceId || (videoDevices.length > 0 ? videoDevices[0].value : ""),
                  ...(!!script && { script: "yes" })
                }}
                onSubmit={() => {}}
              >
                <SelectField
                  ariaLabel={t("Choose camera or screen to record")}
                  imageIconPath="/account.svg"
                  name="recordOption"
                  onChange={selectRecordMethod}
                  options={[
                    { label: t("Record with camera"), value: "camera" },
                    { label: t("Record the screen"), value: "screen" }
                  ]}
                  required
                >
                  <Trans>Choose camera or screen</Trans>
                </SelectField>
                {status !== "acquiring_media" && (
                  <>
                    <SelectField
                      ariaLabel={t("Select audio device to record")}
                      imageIconPath="/microphone.svg"
                      name="audio"
                      options={audioDevices}
                      onChange={selectedAudioDevice}
                      value={audioValueInit}
                      required
                    >
                      <Trans>Audio and Video Settings</Trans>
                    </SelectField>
                    {!screenRecord && (
                      <SelectField
                        ariaLabel={t("Select video device to record")}
                        imageIconPath="/video-camera.svg"
                        name="video"
                        options={videoDevices}
                        onChange={selectedVideoDevice}
                        value={videoValueInit}
                        required
                      >
                        <></>
                      </SelectField>
                    )}
                    {!!script
                    && (
                      <SelectField
                        ariaLabel={t("Select yes if you want to see the magic script while recording")}
                        imageIconPath="/recorder-script.svg"
                        name="script"
                        options={[
                          { value: "yes", label: t("Yes") },
                          { value: "no", label: t("No") }
                        ]}
                        onChange={handleShowScriptChange}
                      >
                        <Trans>Show Magic Script</Trans>
                      </SelectField>
                    )}
                  </>
                )}
                <div className="Button-Container Button-Container__Regular">
                  <Button className="Button-Back" inverted primary onClick={onCloseModal}>
                    <Trans>Go Back</Trans>
                  </Button>
                  {status !== "acquiring_media" && (
                    <Button className="Button-Record" primary onClick={startRecord}>
                      <Trans>{showScript && !screenRecord ? "Start Preview" : "Start Recording"}</Trans>
                    </Button>
                  )}
                </div>
              </Form>
            )}
            <div className="Video-Preview__Container">
              {!videoEditorOpen && (status === "recording" || status === "paused") && (
                <>
                  {!showCountdown && showFullscreen && (status === "recording" || status === "paused") && <Timer pause={isPaused} />}
                  <video className="Video-Preview__Source" ref={videoRef} width={showFullscreen ? 1080 : 600} autoPlay playsInline />
                  {showFullscreen && (
                    <>
                      {!screenRecord && (
                        <Prompter
                          editableScript={editableScript}
                          setEditableScript={setEditableScript}
                          setShowScript={setShowScript}
                          showScript={showScript}
                        />
                      )}
                      <div className="Button-Container Button-Container__Regular">
                        <Button
                          aria-label={t("Cancel record")}
                          onClick={onCloseModal}
                          danger
                          icon="cancel"
                        >
                          <Trans>Cancel</Trans>
                        </Button>
                        {!showCountdown && (
                          <>
                            {!screenRecord && !isPaused && (
                              <Button
                                aria-label={t("Pause record")}
                                onClick={handlePause}
                                icon="pause"
                                className="Button-Gray"
                              >
                                <Trans>Pause</Trans>
                              </Button>
                            )}
                            {((!screenRecord && isPaused && !script)
                            || (script && !previewScript && isPaused))
                            && (
                              <Button
                                aria-label={t("Resume record")}
                                onClick={handleResume}
                                icon="play-filled"
                                className="Button-Gray"
                              >
                                <Trans>Resume</Trans>
                              </Button>
                            )}
                            {previewScript
                            && (
                              <Button onClick={handleResume} icon="checkmark-filled">
                                <Trans>Start Recording</Trans>
                              </Button>
                            )}
                            { (!script || !previewScript)
                            && (
                              <Button
                                className="Button-Record"
                                aria-label={t("Finish record")}
                                onClick={finishRecord}
                                icon="checkmark-filled"
                              >
                                <Trans>Finish</Trans>
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
              {videoEditorOpen && (
                <GetVideoThumbnail
                  video={mediaBlobUrl || ""}
                  onEdit={handleVideoEdited}
                />
              )}
              {!screenRecord && <Loader loading={status !== "recording" && status !== "paused" && !videoEditorOpen && !showCountdown} />}
            </div>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
      <Button className={className} onClick={onOpen}>
        {children}
      </Button>
    </>
  );
};
export default RegularCameraRecorder;
